import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DashboardGetStudentExitSlipQuery } from '../../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import { getStudentExitSlip } from '../../graphQLOperations/Dashboard';
import {
  selectedExitSlipStudentFinished,
  setExitSlipStudentFinished,
} from '../../redux/reducers/exitSlipStudentFinishedSlice';
import { ExitSlipResult } from '../../models/ExitSlip';
import DashboardExitSlipBanner from './DashboardExitSlipBanner';
import DashboardExitSlipCollapsedBanner from './DashboardExitSlipCollapsedBanner';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setExitSlipDoneCountByTarget } from '../../redux/reducers/exitSlipDoneCountSlice';
import { setExitSlipPendingFinish } from '../../redux/reducers/exitSlipPendingFinishSlice';
import { setExitSlipEarnedSkillIds } from '../../redux/reducers/exitSlipEarnedSkillsSlice';
import { selectExitSlipSkillProgressFor } from '../../redux/reducers/exitSlipSkillProgressSlice';
import { selectedStudentSkillStates } from '../../redux/reducers/studentSkillsSlice';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';
import { isSkillOnFire } from '../../businessLogicService/StudentDashboardAux/skillExitSlipProgress';
import { setExitSlipUnlockedSkillIds } from '../../redux/reducers/exitSlipUnlockedSkillsSlice';
import { selectedSkillEarningExitSlipIds } from '../../redux/reducers/exitSlipSkillEarningSlice';
import { selectUser } from '../../redux/reducers/userSlice';
import {
  clearPendingExitSlipFinish,
  EXIT_SLIP_PASSING_RATIO,
  exitSlipCtaStep,
  exitSlipScoreRatio,
  isPendingFinishFor,
  readDismissedExitSlipId,
  readPendingExitSlipFinish,
  resolveExitSlipOutcome,
  summarizeStudentExitSlips,
  writeDismissedExitSlipId,
  writePendingExitSlipFinish,
} from '../../businessLogicService/StudentDashboardAux/exitSlipOutcome';
import { getDateByUserTimezone } from '../../businessLogicService/getDateByUserTimezone';
import { isExitSlipWeeklyGoalEligible } from '../../businessLogicService/weeklyGoalEligibility';
import { useWeeklyGoal } from '../../contexts/WeeklyGoalContext';
import { SKILL_RESOURCE_STEP_PARAM } from '../../models/Timeline';

interface Props {
  queryReferenceExitSlip?: PreloadedQuery<DashboardGetStudentExitSlipQuery, Record<string, unknown>>;
}
const DashboardExitSlipQuery: FC<Props> = ({ queryReferenceExitSlip }) => {
  const dispatch = useAppDispatch();
  const userRedux = useAppSelector(selectUser);
  const navigate = useNavigate();
  const exitSlipFinishedRedux = useAppSelector(selectedExitSlipStudentFinished);
  // The result the modal hands back the moment the student finishes. The query was fetched before they
  // took the ticket, so for that one render it is the only place the score exists; a reload then gets
  // it from the completed session instead.
  const [finishedResult, setFinishedResult] = useState<{
    exitSlipId: string;
    skillId?: string;
    result: ExitSlipResult[];
  } | null>(null);
  // Both records below are stored per student, because localStorage belongs to the device and a
  // classroom device is shared. The component only mounts once the query has been loaded, which
  // DashboardStudent does only for a known user, so this is set by the time either is read.
  const studentId = userRedux?.id;
  const [dismissedExitSlipId, setDismissedExitSlipId] = useState<string | null>(() =>
    readDismissedExitSlipId(studentId),
  );
  // A finish this device has seen but the server has not reported back yet — seeded from localStorage so
  // both the result card and the badge survive leaving the dashboard before the query catches up.
  const [pendingFinish, setPendingFinish] = useState(() => readPendingExitSlipFinish(studentId));

  const queryData = usePreloadedQuery<DashboardGetStudentExitSlipQuery>(
    getStudentExitSlip,
    queryReferenceExitSlip as PreloadedQuery<DashboardGetStudentExitSlipQuery>,
  );

  useEffect(() => {
    if (!exitSlipFinishedRedux) return;
    dispatch(setExitSlipStudentFinished(false));
  }, [exitSlipFinishedRedux]);

  // The query now returns every ticket due up to today (see the loader in DashboardStudent), so it
  // serves two readers at once: the unit header's badge, which counts the tickets the student has
  // passed in the unit on screen, and this component, which only ever shows today's ticket.
  const todayKey = format(getDateByUserTimezone(userRedux?.activeTimeZone || '').queryDate, 'yyyy-MM-dd');
  const { doneExitSlipIds, passedExitSlipIdsByTarget, completedTicketSkillIds, todaysExitSlip } = useMemo(
    () => summarizeStudentExitSlips(queryData.classExitSlips, todayKey),
    [queryData, todayKey],
  );

  // The stored record only stands while the query has not caught up. Deriving that here rather than
  // waiting for the cleanup effect below keeps the badge off by one for exactly zero renders.
  // Measured against every finished ticket, not only passed ones: the record is written whatever the
  // score, so a narrower list would leave a failed ticket's record looking forever unreported.
  const pendingStillNeeded = !!pendingFinish && !doneExitSlipIds.includes(pendingFinish.exitSlipId);

  // Counted against the ticket's own unit, which is not always the unit on screen — finishing a
  // ticket while browsing elsewhere moves that unit's badge, not the visible one. A record from
  // before the badge was unit-scoped has no target and so moves nothing; see PendingExitSlipFinish.
  const passedCountByTarget = useMemo(() => {
    const counts = Object.fromEntries(
      Object.entries(passedExitSlipIdsByTarget).map(([targetId, ids]) => [targetId, ids.length]),
    );
    const pendingPassed = pendingStillNeeded && !!pendingFinish && pendingFinish.scoreRatio >= EXIT_SLIP_PASSING_RATIO;
    const pendingTargetId = pendingPassed ? pendingFinish.targetId : undefined;
    return pendingTargetId ? { ...counts, [pendingTargetId]: (counts[pendingTargetId] ?? 0) + 1 } : counts;
  }, [passedExitSlipIdsByTarget, pendingStillNeeded, pendingFinish]);

  useEffect(() => {
    dispatch(setExitSlipDoneCountByTarget(passedCountByTarget));
  }, [dispatch, passedCountByTarget]);

  // The same record, published for the unit-path cards' badges. Their report was fetched once when
  // the dashboard opened and cannot see a finish made since; this is how it hears about one. Sent as
  // null the moment the record stops being needed, so the badge drops back to the server's own count
  // in the same breath as the header does.
  useEffect(() => {
    dispatch(setExitSlipPendingFinish(pendingStillNeeded ? pendingFinish : null));
  }, [dispatch, pendingFinish, pendingStillNeeded]);

  // The query now reports the finish the record was covering for, so it has done its job.
  useEffect(() => {
    if (pendingFinish && !pendingStillNeeded) {
      clearPendingExitSlipFinish(studentId);
      setPendingFinish(null);
    }
  }, [pendingFinish, pendingStillNeeded, studentId]);

  // Sitting a ticket opens its skill's card on the unit path whatever the score. The ticket just
  // finished is added off the freshly graded result too: the query was fetched before the student took
  // it and will not carry its completed session until the page is reloaded.
  const justFinishedSkillId = finishedResult?.skillId;
  const unlockedSkillIds = useMemo(
    () =>
      justFinishedSkillId && !completedTicketSkillIds.includes(justFinishedSkillId)
        ? [...completedTicketSkillIds, justFinishedSkillId]
        : completedTicketSkillIds,
    [completedTicketSkillIds, justFinishedSkillId],
  );

  useEffect(() => {
    dispatch(setExitSlipUnlockedSkillIds(unlockedSkillIds));
  }, [dispatch, unlockedSkillIds]);

  const handleFinished = useCallback(
    (result: ExitSlipResult[]) => {
      if (!todaysExitSlip) {
        return;
      }
      setFinishedResult({
        exitSlipId: todaysExitSlip.id,
        skillId: todaysExitSlip.curriculumLesson.skill?.id,
        result,
      });
      // The finish has to be remembered here and not recomputed later: `finishedResult` is in-memory
      // only, so without this the result card would have no score to show — and the badge would drop
      // back by one — as soon as the student leaves the dashboard while the server is still behind.
      // Stored whatever the score, because the card has to come back for a failed ticket too.
      // The target travels with the record: the badge it moves is the ticket's own unit's, and by the
      // time a reload reads this back there is nothing on screen saying which unit that was.
      const finish = {
        exitSlipId: todaysExitSlip.id,
        scoreRatio: exitSlipScoreRatio(result),
        targetId: todaysExitSlip.targetId ?? undefined,
      };
      setPendingFinish(finish);
      writePendingExitSlipFinish(studentId, finish);
    },
    [todaysExitSlip, studentId],
  );

  const handleDismiss = useCallback(() => {
    if (!todaysExitSlip) {
      return;
    }
    setDismissedExitSlipId(todaysExitSlip.id);
    writeDismissedExitSlipId(studentId, todaysExitSlip.id);
  }, [todaysExitSlip, studentId]);

  const lessonSkill = todaysExitSlip?.curriculumLesson.skill ?? null;

  const hasExitSlip = !!todaysExitSlip?.curriculumLesson.hasExitSlip;
  // The stored record, for today's ticket. Both readings below need it.
  const hasPendingFinishForToday = isPendingFinishFor(pendingFinish, todaysExitSlip?.id);
  // Four sources, and the fourth is the only one that survives the student leaving the dashboard and
  // coming back without a reload: `finishedResult` is in-memory, the redux flag is cleared the moment
  // it is read (see the effect above), and `isComplete` waits on a refetch. Without the stored record
  // here, navigating away and back would offer the invitation again for a ticket already sat.
  const todaysFinishedResult =
    finishedResult && finishedResult.exitSlipId === todaysExitSlip?.id ? finishedResult.result : null;
  const isFinished =
    !!todaysExitSlip?.isComplete || !!todaysFinishedResult || exitSlipFinishedRedux || hasPendingFinishForToday;
  const showInvitation = hasExitSlip && !isFinished;
  // Three sources, most recent first: the result the modal just handed back, the record stored for a
  // finish the server has not reported yet, and the completed session once it has. Only the first is
  // gone after navigating away, which is why the second exists.
  const pendingScoreRatio = hasPendingFinishForToday ? pendingFinish?.scoreRatio : undefined;
  const scoreRatio = todaysFinishedResult
    ? exitSlipScoreRatio(todaysFinishedResult)
    : pendingScoreRatio ?? todaysExitSlip?.scoreRatio ?? null;

  // Tickets that are the last one standing between the student and a skill (see
  // findSkillEarningExitSlipIds, fed by the target-group report). Read twice: as the banner's promise
  // before the student sits today's ticket, and as the result card's skill-earned state after;
  // resolveExitSlipOutcome ignores it below the passing bar.
  const skillEarningExitSlipIds = useAppSelector(selectedSkillEarningExitSlipIds);
  const todaysTicketEarnsSkill = !!todaysExitSlip && skillEarningExitSlipIds.includes(todaysExitSlip.id);

  // The result card follows the card badge's fire: one finish, one verdict on the skill. Both read
  // the same rule off the same report entry, so the banner cannot say "Try Challenge" while the
  // skill's own card declines to catch fire, or the other way round.
  const todaysSkillId = todaysExitSlip?.curriculumLesson.skill?.id;
  const todaysSkillProgress = useAppSelector(state => selectExitSlipSkillProgressFor(state, todaysSkillId));
  const studentSkillStates = useAppSelector(selectedStudentSkillStates);
  const todaysSkillState = studentSkillStates?.find(
    (skill: { skill?: { id?: string } }) => skill.skill?.id === todaysSkillId,
  );
  const todaysSkillAlreadyEarned = !!todaysSkillState && isSkillCompleted(todaysSkillState);
  const todaysSkillOnFire = !!todaysSkillProgress && isSkillOnFire(todaysSkillProgress, todaysSkillAlreadyEarned);

  const outcome =
    scoreRatio !== null
      ? resolveExitSlipOutcome({
          scoreRatio,
          isOnFire: todaysSkillOnFire,
          hasDemonstratedSkill: todaysTicketEarnsSkill,
        })
      : null;

  // The skill this finish has just won, held for the unit-path card to read as Inferred before the
  // server reports the new skill state. Exactly the case the collapsed banner celebrates, so the two
  // are derived from the same outcome rather than from two rules that could drift apart.
  const earnedSkillIds = useMemo(
    () => (outcome === 'passed-and-demonstrated' && todaysSkillId ? [todaysSkillId] : []),
    [outcome, todaysSkillId],
  );

  useEffect(() => {
    dispatch(setExitSlipEarnedSkillIds(earnedSkillIds));
  }, [dispatch, earnedSkillIds]);

  const weeklyGoal = useWeeklyGoal();
  const countsTowardWeeklyGoal =
    outcome === 'passed-and-demonstrated' &&
    !!lessonSkill?.code &&
    isExitSlipWeeklyGoalEligible(todaysSkillState?.skillSubstate?.id ?? null);

  useEffect(() => {
    if (!weeklyGoal || !lessonSkill || !countsTowardWeeklyGoal) return;
    if (weeklyGoal.overrideData?.some(goal => goal.code === lessonSkill.code)) return;
    weeklyGoal.addGoalOverride({
      code: lessonSkill.code,
      name: lessonSkill.name,
      skillType: todaysSkillState?.targetSkillType || 'Support',
    });
  }, [weeklyGoal, lessonSkill, countsTowardWeeklyGoal, todaysSkillState]);

  const handleGoToSkill = useCallback(() => {
    if (!lessonSkill?.code || !outcome) {
      return;
    }
    // Straight to the skill's own page, opened on the menu option the outcome calls for (see
    // exitSlipCtaStep).
    const step = exitSlipCtaStep[outcome];
    navigate(step ? `/skill/${lessonSkill.code}?${SKILL_RESOURCE_STEP_PARAM}=${step}` : `/skill/${lessonSkill.code}`);
  }, [lessonSkill, outcome, navigate]);

  // The CTA needs a skill page to open, which means a code. The query maps it to '' when the lesson
  // carries no skill at all.
  const canGoToSkill = !!lessonSkill?.code;

  // Whether the invitation was on screen a render ago. The banner and the card swap in a single
  // commit (DashboardExitSlipModal's closeAfterFinished batches all three state changes), so this
  // separates "the student just finished and we are morphing" from "the card is mounting cold on a
  // page load" — only the former holds the card's content back while the box travels.
  const wasShowingInvitation = useRef(false);
  const arrivingFromBanner = wasShowingInvitation.current && !showInvitation;
  useEffect(() => {
    wasShowingInvitation.current = showInvitation;
  });

  if (!todaysExitSlip || !hasExitSlip) {
    return null;
  }

  // Closing the card is the one way a ticket for today ends up with no banner at all — an explicit
  // choice by the student, which the always-a-banner rule below deliberately does not override.
  if (dismissedExitSlipId === todaysExitSlip.id) {
    return null;
  }

  // Finished, and we know how they did: the result card in the corner.
  if (!showInvitation && outcome !== null && scoreRatio !== null) {
    return (
      <DashboardExitSlipCollapsedBanner
        outcome={outcome}
        scoreRatio={scoreRatio}
        onGoToSkill={canGoToSkill ? handleGoToSkill : null}
        onDismiss={handleDismiss}
        arrivingFromBanner={arrivingFromBanner}
        skillName={lessonSkill?.name}
      />
    );
  }

  // A ticket for today always has a banner, so the invitation is the fallback rather than an early
  // return. It covers the ticket still being open, and also the narrow case of knowing the student
  // finished but having no score to show: redux says finished, the query has not caught up, and
  // localStorage was unavailable to carry the result across. Offering the ticket again is recoverable;
  // rendering nothing, which is what used to happen here, left the corner empty for the rest of the day.
  return (
    <div
      className="tw-flex tw-w-full tw-right-[0] lg:tw-right-[unset] lg:tw-w-4/5 tw-fixed tw-z-30 tw-justify-center tw-pointer-events-none"
      style={{ bottom: '80px' }}
    >
      <DashboardExitSlipBanner
        exitSlipsData={todaysExitSlip}
        onFinished={handleFinished}
        isSkillEarning={todaysTicketEarnsSkill}
      />
    </div>
  );
};

export default DashboardExitSlipQuery;
